import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { numberToCurrency } from 'utils/misc';
import { Link } from 'react-router-dom';
import Table from 'components/ui/Table';
import { TextButton, InfoWidget } from '@ds/react-components';
import { List as ListLoader } from 'react-content-loader';
import { groupTransactionsByPaymentId } from 'utils/transactions';

const styles = (theme) => ({
  root: {},
  tableHead: {
    '& tr th': {
      padding: '0px 15px 15px 15px',
    },
  },
  amountPos: {
    color: theme.palette.xpl.green[700],
  },
  amountNeg: {
    color: theme.palette.xpl.message.negative.default,
  },
  loader: {
    height: 150,
    width: '100%',
  },
  transDate: {
    color: theme.palette.xpl.text.primary,
  },
});

const tableMetadata = [
  {
    id: 'transDate',
    renderingField: 'transDateStr',
    width: '10%',
    numeric: false,
    disablePadding: false,
    label: 'Date',
    alignment: 'left',
  },
  {
    id: 'paymentType',
    renderingField: 'paymentTypeStr',
    width: '20%',
    numeric: false,
    disablePadding: false,
    label: 'Payment method',
    alignment: 'left',
  },
  {
    id: 'transcode',
    renderingField: 'transcodeStr',
    width: '20%',
    numeric: false,
    hideInMobile: true,
    disablePadding: false,
    label: 'Description',
    alignment: 'left',
  },
  {
    id: 'amount',
    renderingField: 'amountFld',
    width: '10%',
    numeric: false,
    disablePadding: false,
    label: 'Amount',
    alignment: 'left',
  },
  {
    id: 'errorCode',
    renderingField: 'errorCodeStr',
    width: '10%',
    numeric: false,
    hideInMobile: true,
    disablePadding: false,
    label: 'Details',
    alignment: 'left',
  },
];

const TransactionsPreview = (props) => {
  const { classes, customerDetails, localisation } = props;
  const { adfitNo, transactions } = customerDetails;

  const trans = Array.isArray(transactions) ? transactions : [];
  const groupedTransactions = groupTransactionsByPaymentId(trans);
  const amountFld = (amount, classes, localisation) => (
    <Typography className={amount < 0 ? classes.amountNeg : classes.amountPos}>
      {amount < 0 ? '-' : '+'}
      {numberToCurrency(Math.abs(amount), localisation)}
    </Typography>
  );

  const createRowItems = (transactions, classes, localisation) => {
    const { dateFormat } = localisation.locales;
    const overviewTableLength = 3;
    let result = transactions.map((item, index) => {
      // Date
      item.transDateStr = (
        <Typography className={classes.transDate}>
          {moment(item.transDate).format(dateFormat)}
        </Typography>
      );

      // Payment method
      item.paymentTypeStr =
        localisation.prompts.paymentType[item.paymentType] || 'Other';

      // Description
      if (item.isCombinedPayment) {
        item.transcodeStr = 'Combined payment...';
        item.__childrens.forEach((childItem) => {
          childItem.transcodeStr =
            localisation.prompts.transactionCode[childItem.transcode];
          childItem.amountFld = amountFld(
            childItem.amount,
            classes,
            localisation
          );
        });
      } else {
        item.transcodeStr =
          localisation.prompts.transactionCode[item.transcode];
      }

      // Amount
      item.amountFld = amountFld(item.amount, classes, localisation);

      // Details
      item.errorCodeStr = localisation.prompts.transErrorCode[item.errorCode];
      if (item.retryCount > 0) {
        item.errorCodeStr = 'Payment Retry';
      }

      return item;
    });
    return result.slice(0, overviewTableLength);
  };

  const data = useMemo(
    () => createRowItems(groupedTransactions, classes, localisation),
    [classes, createRowItems, groupedTransactions, localisation, trans]
  );

  return (
    <InfoWidget
      title="Transactions"
      contentLoader={<ListLoader className={classes.loader} />}
      actionButton={
        <Link to={`/Customers/${adfitNo}/Transactions`}>
          <TextButton>{' View All'}</TextButton>
        </Link>
      }
      isLoading={!adfitNo || transactions == null}
      classes={{ root: classes.root }}>
      <Table
        data={data}
        metadata={tableMetadata}
        noBackground={true}
        enableSorting={false}
        showFooter={false}
        classes={{ table: classes.tableHead }}
      />
    </InfoWidget>
  );
};

TransactionsPreview.propTypes = {
  classes: PropTypes.object.isRequired,
  customerDetails: PropTypes.object,
};

export default withStyles(styles)(TransactionsPreview);
